import { Button } from '@mui/material';
import { getMetricSpec, WorkoutSet } from '@strength/domain-workout';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useWorkoutContext } from '../../context/WorkoutContext';
import { AddMetricsModal } from './AddMetricsModal';
import { TabletMetricInput } from './CreateEditExerciseMetricInput';
import {
  CreateEditExerciseMetrics,
  createEditExerciseTabletSchema,
} from './types';

type TabletMetricsFormInputProps = {
  form: UseFormReturn<z.infer<typeof createEditExerciseTabletSchema>>;
  value: CreateEditExerciseMetrics[];
  onChange: (metrics: CreateEditExerciseMetrics[]) => void;
  sets?: WorkoutSet[];
};

export const TabletMetricsFormInput = ({
  form,
  value,
  onChange,
  sets,
}: TabletMetricsFormInputProps) => {
  const [isAddMetricModalOpen, setIsAddMetricModalOpen] = useState(false);
  const { getMetric, getUnitsForMetric } = useWorkoutContext();
  const { t } = useTranslation();

  const handleOpenAddMetricModal = () => {
    setIsAddMetricModalOpen(true);
  };

  const handleCloseAddMetricModal = () => {
    setIsAddMetricModalOpen(false);
  };

  const handleAddSet = (
    onChange: (value: CreateEditExerciseMetrics[]) => void,
  ) => {
    const metrics = form.getValues('metrics');

    onChange(
      metrics.map((m) => {
        // use the first set metric unit as the default unit if it exists
        const firstSetMetricUnit = m.inputsForSet[0]?.unit;
        const defaultUnit = getUnitsForMetric(m.metricId)?.[0] ?? '';

        const unit = firstSetMetricUnit ?? defaultUnit;
        return {
          ...m,
          inputsForSet: [
            ...m.inputsForSet,
            {
              setIndex: m.inputsForSet.length,
              value: '',
              unit,
            },
          ],
        };
      }),
    );
  };

  const handleRemoveSet = (
    onChange: (value: CreateEditExerciseMetrics[]) => void,
  ) => {
    const metrics = form.getValues('metrics');

    onChange(
      metrics.map((metric) => {
        metric.inputsForSet.pop();
        return metric;
      }),
    );
  };

  // onBlur validate that the metric value is valid
  // if valid remove the error
  const validateMetricValue = (
    valueWithUnits: string,
    setIndex: number,
    metricId: string,
  ) => {
    const spec = getMetricSpec(getMetric(metricId));
    const metrics = form.getValues('metrics');
    if (!spec.schema.safeParse(valueWithUnits).success) {
      // set the error
      form.setValue(
        'metrics',
        metrics.map((m) => {
          if (m.metricId === metricId) {
            return {
              ...m,
              inputsForSet: m.inputsForSet.map((i) => {
                // Inputs in tablet are preset when a user selects an exercise,
                // if they want to leave the input blank they are allowed to do so
                // but the safeParse() will fail because the value is empty, but the unit is still valid
                // we want to add a check that if the value is empty, we don't show an error
                if (i.value?.length === 0) {
                  return i;
                }
                if (i.setIndex === setIndex) {
                  return { ...i, error: 'Invalid metric value' };
                }
                return i;
              }),
            };
          }
          return m;
        }),
      );
    } else {
      // clear the error
      form.setValue(
        'metrics',
        metrics.map((m) => {
          if (m.metricId === metricId) {
            return {
              ...m,
              inputsForSet: m.inputsForSet.map((i) => {
                if (i.setIndex === setIndex) {
                  return { ...i, error: undefined };
                }
                return i;
              }),
            };
          }
          return m;
        }),
      );
    }
  };

  return (
    <div className="min-w-0 w-fit max-w-full">
      <div className="flex flex-row gap-4 overflow-x-auto pb-2">
        {value.map((metric) => (
          <div
            key={metric.metricId}
            className="border border-gray-300 rounded-md pt-2 pb-4 px-4 flex flex-col gap-2"
          >
            <div className="flex flex-row font-semibold text-xs whitespace-nowrap">
              {metric.name}
            </div>

            <div className="flex flex-col gap-4">
              {metric.inputsForSet.map((input) => (
                <div key={input.setIndex}>
                  <TabletMetricInput
                    setIndex={input.setIndex}
                    metricId={metric.metricId}
                    input={input}
                    metricType={metric.type}
                    onBlur={validateMetricValue}
                    onEditSetMetric={(_setIndex, inputValue) => {
                      // Get the workoutSetGroupId of the edited set
                      const editedGroupId = input.workoutSetGroupId;
                      onChange(
                        value.map((m) =>
                          m.metricId === metric.metricId
                            ? {
                                ...m,
                                // Update all sets in the same group (for grouped/expanded sets);
                                // without a group, only the edited set changes — otherwise every
                                // undefined group id would match and one edit would rewrite them all
                                inputsForSet: m.inputsForSet.map((set) =>
                                  editedGroupId !== undefined &&
                                  set.workoutSetGroupId === editedGroupId
                                    ? {
                                        ...set,
                                        value: inputValue.value ?? '',
                                        unit: inputValue.unit ?? '',
                                      }
                                    : set.setIndex === input.setIndex
                                      ? {
                                          ...set,
                                          value: inputValue.value ?? '',
                                          unit: inputValue.unit ?? '',
                                        }
                                      : set,
                                ),
                              }
                            : m,
                        ),
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-row gap-4 justify-between mt-4">
        <div className="flex gap-2">
          <Button variant="outlined" onClick={() => handleAddSet(onChange)}>
            {t('addSet')}
          </Button>
          <Button
            variant="text"
            onClick={() => handleRemoveSet(onChange)}
            disabled={Math.min(...value.map((m) => m.inputsForSet.length)) <= 1}
          >
            {t('removeSet')}
          </Button>
        </div>
        <AddMetricsModal
          isOpen={isAddMetricModalOpen}
          onOpen={handleOpenAddMetricModal}
          onClose={handleCloseAddMetricModal}
          selectedMetrics={value}
          onSave={onChange}
          sets={sets}
        />
      </div>
    </div>
  );
};
